<template>
  <v-app>
    <v-navigation-drawer
      v-model="drawer"
      :mini-variant.sync="mini"
      :width="300"
      app
      permanent
    >
      <!-- Cabeçalho "Calendário" -->
      <div v-if="!mini" class="drawer-section-header">
        <v-icon small color="primary" class="mr-2">mdi-calendar</v-icon>
        <span class="drawer-section-title">Calendário</span>
      </div>

      <v-divider />

      <!-- Date picker -->
      <div class="date-picker-wrapper" data-tour="agenda-date-picker">
        <v-date-picker
          v-model="calendarPageSelectedDate"
          :picker-date="pickerDate"
          :no-title="true"
          locale="pt-BR"
          color="primary"
          flat
          full-width
          show-adjacent-months
          @input="onDateSelected"
        />
      </div>

      <v-divider />

      <!-- Ações -->
      <div v-if="!mini" class="drawer-section-header">
        <v-icon small color="primary" class="mr-2">mdi-lightning-bolt</v-icon>
        <span class="drawer-section-title">Ações</span>
      </div>

      <v-list dense class="px-2">
        <v-list-item
          class="action-item action-primary"
          link
          data-tour="agenda-novo-atendimento"
          @click="openAddEventDialog"
        >
          <v-list-item-icon class="action-icon">
            <v-icon color="white">mdi-calendar-plus</v-icon>
          </v-list-item-icon>
          <v-list-item-content>
            <v-list-item-title class="action-title">
              Agendamento
            </v-list-item-title>
          </v-list-item-content>
        </v-list-item>

        <!-- salvia-772: compromisso do médico. Fica ao lado do agendamento de
             propósito (é a mesma decisão: "vou ocupar este horário"), mas com
             ícone e cor próprios para ninguém confundir os dois fluxos. -->
        <v-list-item
          v-if="isDoctor"
          class="action-item action-commitment"
          link
          data-tour="agenda-compromisso"
          @click="openCommitmentDialog()"
        >
          <v-list-item-icon class="action-icon">
            <v-icon color="white">mdi-calendar-account</v-icon>
          </v-list-item-icon>
          <v-list-item-content>
            <v-list-item-title class="action-title">
              Adicionar compromisso
            </v-list-item-title>
          </v-list-item-content>
        </v-list-item>

        <v-list-item
          class="action-item action-success"
          link
          data-tour="agenda-novo-paciente"
          @click="openAddNewUserDialog"
        >
          <v-list-item-icon class="action-icon">
            <v-icon color="white">mdi-account-plus</v-icon>
          </v-list-item-icon>
          <v-list-item-content>
            <v-list-item-title class="action-title">
              Adicionar novo paciente
            </v-list-item-title>
          </v-list-item-content>
        </v-list-item>

        <v-list-item
          v-if="isDoctor"
          class="action-item action-warning"
          link
          data-tour="agenda-pauta"
          @click="openPautaDialog"
        >
          <v-list-item-icon class="action-icon">
            <v-icon color="white">mdi-lightbulb-on-outline</v-icon>
          </v-list-item-icon>
          <v-list-item-content>
            <v-list-item-title class="action-title">
              Pauta Inteligente
            </v-list-item-title>
          </v-list-item-content>
        </v-list-item>

        <!-- Pendências de encaixe (assistente) -->
        <v-list-item
          v-if="isAssistant"
          class="action-item action-fitin"
          link
          data-tour="agenda-encaixe"
          @click="fitInDialog = true"
        >
          <v-list-item-icon class="action-icon">
            <v-badge
              :value="fitInPendingCount > 0"
              :content="fitInPendingCount"
              color="red"
              overlap
            >
              <v-icon color="white">mdi-calendar-clock</v-icon>
            </v-badge>
          </v-list-item-icon>
          <v-list-item-content>
            <v-list-item-title class="action-title">
              Pendências de encaixe
            </v-list-item-title>
          </v-list-item-content>
        </v-list-item>
      </v-list>

      <v-divider class="mt-2" />

      <!-- Programação -->
      <div v-if="!mini" class="drawer-section-header">
        <v-icon small color="primary" class="mr-2">mdi-view-dashboard</v-icon>
        <span class="drawer-section-title">Programação</span>
      </div>

      <v-list dense class="section-list" data-tour="agenda-programacao">
        <v-list-item
          v-for="item in items"
          :key="item.title"
          link
          class="section-item"
          :class="{ 'active-item': selectedView === item.view }"
          @click="selectMenuOptionCalendar(item.view)"
        >
          <v-list-item-icon class="info-icon">
            <v-icon
              :color="selectedView === item.view ? 'primary' : 'grey darken-1'"
            >
              {{ item.icon }}
            </v-icon>
          </v-list-item-icon>
          <v-list-item-content v-if="!mini">
            <v-list-item-title class="section-title">
              {{ item.title }}
            </v-list-item-title>
          </v-list-item-content>
          <v-list-item-action v-if="!mini">
            <v-icon
              small
              :color="selectedView === item.view ? 'primary' : 'grey lighten-1'"
            >
              {{
                selectedView === item.view
                  ? "mdi-chevron-right"
                  : "mdi-chevron-right"
              }}
            </v-icon>
          </v-list-item-action>
        </v-list-item>
      </v-list>
    </v-navigation-drawer>

    <v-container fluid data-tour="agenda-calendario">
      <component
        :is="currentMainContentViewComponent"
        :type.sync="selectedView"
        :current-date.sync="calendarPageSelectedDate"
        :mode="$route.query.mode || 'doctor'"
        @reset-calendar-to-today="resetToToday"
        @reset-calendar-to-day-type="resetToDayType"
        @calendar-year-date-selected="switchToDayMode"
        @time-clicked="handleTimeClicked"
        ref="calendarPageContent"
      />
    </v-container>

    <CommitmentDialog
      v-model="commitmentDialog.visible"
      :commitment="commitmentDialog.item"
      @saved="updateCalendar"
    />

    <CalendarDialogAddNewEvent
      v-model="openDialogAddNewEvent"
      :pre-selected-date="selectedTimeForNewEvent ? selectedTimeForNewEvent.date : null"
      :pre-selected-time="selectedTimeForNewEvent ? selectedTimeForNewEvent.time : null"
      @eventSaved="updateCalendar"
    />

    <AddNewUserDialog
      v-model="addNewUserDialog.visible"
      :user-id="addNewUserDialog.userId"
      :target-type="addNewUserDialog.targetType"
    />

    <PautaInteligenteDialog
      v-model="pautaDialog.visible"
      :doctor-id="doctorId"
    />

    <FitInPendingDialog
      v-model="fitInDialog"
      @updated="loadFitInCount"
      @count="fitInPendingCount = $event"
    />

    <!--<div
      class="d-flex"
      style="position: fixed; bottom: 80px; right: 16px; gap: 16px; z-index: 10"
    >
      <v-tooltip bottom>
        <template v-slot:activator="{ on, attrs }">
          <v-btn
            fab
            color="green darken-1"
            dark
            v-bind="attrs"
            v-on="on"
            @click="openAddNewUserDialog"
          >
            <v-icon>mdi-account-plus</v-icon>
          </v-btn>
        </template>
        <span>Adicionar novo paciente</span>
      </v-tooltip>

      <v-tooltip bottom>
        <template v-slot:activator="{ on, attrs }">
          <v-btn
            fab
            color="blue darken-1"
            dark
            v-bind="attrs"
            v-on="on"
            @click="openAddEventDialog"
          >
            <v-icon>mdi-calendar-plus</v-icon>
          </v-btn>
        </template>
        <span>Adicionar novo atendimento</span>
      </v-tooltip>
    </div>-->
  </v-app>
</template>

<script>
import http from "@/services/http"; // ✅ usa interceptor
import { getLocalDateISO } from "@/utils/currentDayDate";

import CalendarPageContent from "@/features/calendar/components/CalendarPageContent.vue";
import CalendarPageContentDayKanban from "@/features/calendar/components/CalendarPageContentDayKanban.vue";
import CalendarPageContentYear from "@/features/calendar/components/CalendarPageContentYear.vue";
import CalendarDialogAddNewEvent from "@/features/calendar/components/CalendarDialogAddNewEvent.vue";
import CommitmentDialog from "@/features/calendar/components/CommitmentDialog.vue";
import AddNewUserDialog from "@/features/patients/add-user/AddNewUserDialog.vue";
import PautaInteligenteDialog from "@/features/patients/persons/PautaInteligenteDialog.vue";
import FitInPendingDialog from "@/features/calendar/components/FitInPendingDialog.vue";
import { getPendingFitIns } from "@/services/fitInRequestService";
import tourMixin from "@/features/tour/tourMixin";

export default {
  mixins: [tourMixin],

  components: {
    CalendarDialogAddNewEvent,
    CommitmentDialog,
    PautaInteligenteDialog,
    FitInPendingDialog,
    AddNewUserDialog,
    CalendarPageContent,
    CalendarPageContentDayKanban,
    CalendarPageContentYear,
  },

  data() {
    const today = getLocalDateISO();

    return {
      selectedView: "week", // SALVIA-932: a agenda abre na semana, para todos os perfis
      fitInDialog: false,
      fitInPendingCount: 0,
      items: [
        { title: "Dia", icon: "mdi-calendar-today", view: "day" },
        {
          title: "Dia kanban",
          icon: "mdi-view-column-outline",
          view: "day-kanban",
        },
        { title: "Semana", icon: "mdi-calendar-week", view: "week" },
        { title: "Mês", icon: "mdi-calendar-month", view: "month" },
        { title: "Ano", icon: "mdi-calendar-blank", view: "year" },
      ],

      calendarPageSelectedDate: today, // ✅ fonte única de verdade
      pickerDate: null,
      mini: false,
      drawer: false,
      // salvia-772: `item` null abre em modo criação; preenchido, em edição.
      commitmentDialog: { visible: false, item: null },

      openDialogAddNewEvent: false,
      selectedTimeForNewEvent: null,

      addNewUserDialog: {
        visible: false,
        userId: null,
        targetType: "patient",
      },

      pautaDialog: {
        visible: false,
      },
    };
  },

  watch: {
    openDialogAddNewEvent(val) {
      if (!val) {
        this.selectedTimeForNewEvent = null;
      }
    }
  },

  computed: {
    tourId() {
      return "agenda";
    },
    doctorId() {
      return this.$store.getters["auth/userId"];
    },
    isDoctor() {
      const t = this.$store.getters["auth/userType"];
      return t === "doctor_solo" || t === "doctor_clinic";
    },
    isAssistant() {
      return this.$store.getters["auth/userType"] === "assistant";
    },
    currentMainContentViewComponent() {
      switch (this.selectedView) {
        case "day":
        case "week":
        case "month":
          return "CalendarPageContent";
        case "day-kanban":
          return "CalendarPageContentDayKanban";
        case "year":
          return "CalendarPageContentYear";
        default:
          return "CalendarPageContent";
      }
    },
  },

  mounted() {
    if (this.isAssistant) this.loadFitInCount();
  },

  methods: {
    // Passos do tour guiado desta página (adaptados ao papel do usuário).
    // startTour() vem do tourMixin (botão "?").
    tourSteps() {
      const steps = [
        {
          // Passo de boas-vindas (sem âncora → card centralizado).
          title: "Bem-vindo à Agenda 👋",
          description:
            "Este é o seu centro de agendamentos. Em poucos passos vou mostrar como navegar pelas datas, marcar atendimentos, cadastrar pacientes e trocar de visualização. Você pode reabrir este tour a qualquer momento pelo botão “?”.",
        },
        {
          anchor: "agenda-date-picker",
          title: "Navegue pelas datas",
          description:
            "Use o mini-calendário para saltar para qualquer dia. A agenda ao lado acompanha a data escolhida. Os dias em que você atende aparecem destacados em verde quando há disponibilidade configurada.",
        },
        {
          anchor: "agenda-novo-atendimento",
          title: "Agende um atendimento",
          description:
            "Abre o formulário para marcar uma consulta: paciente, clínica, data/horário e modalidade (presencial ou online). Dá também para sinalizar “Deseja encaixe?”, avisando que o paciente aceita ser chamado mais cedo se abrir vaga.",
        },
        {
          // Explicação do pré-requisito de Conta Google (sem âncora → centralizado).
          title: "Antes de agendar: Conta Google",
          description:
            "A agenda cria os eventos direto no Google Calendar. Por isso, a clínica precisa ter uma Conta Google vinculada — isso é feito em “Minhas Clínicas”, no card da clínica. Enquanto a clínica não estiver vinculada, ela aparece desabilitada no formulário e não é possível marcar atendimentos nela.",
        },
        {
          // Só aparece quando a agenda está bloqueada por falta de vínculo.
          anchor: "cal-vincular",
          title: "Vincule sua conta ao Google",
          description:
            "Sua conta (ou, no modo Clínica, a clínica) ainda não está vinculada ao Google, então a agenda fica bloqueada. Clique em “Vincular Conta Google” e faça o login: sem esse vínculo não é possível ver nem criar atendimentos. É o primeiro passo para usar a agenda.",
        },
        {
          anchor: "agenda-novo-paciente",
          title: "Cadastre um paciente",
          description:
            "Adicione um paciente sem sair da agenda. O cadastro começa pelo CPF: se ele já existir no sistema, você só o vincula; se não, faz o cadastro completo. Depois ele já fica disponível para agendamento.",
        },
      ];

      if (this.isDoctor) {
        steps.push({
          anchor: "agenda-pauta",
          title: "Pauta Inteligente",
          description:
            "Sugere quem chamar para a agenda com base no histórico e nas pendências dos seus pacientes — ajuda a preencher horários vagos com quem mais precisa de retorno.",
        });
      }
      if (this.isAssistant) {
        steps.push({
          anchor: "agenda-encaixe",
          title: "Pendências de encaixe",
          description:
            "Os pacientes que pediram encaixe aparecem aqui. Quando um horário vagar, você consegue acomodá-los rapidamente a partir desta lista.",
        });
      }

      steps.push(
        {
          anchor: "agenda-programacao",
          title: "Modos de visualização",
          description:
            "Alterne entre Dia, Semana, Mês, Kanban e Ano. Cada modo mostra a agenda de um jeito: o Kanban, por exemplo, organiza os atendimentos do dia em colunas por status (Em Espera, Em Atendimento, etc.).",
        },
        // Passos do modo Clínica (as âncoras só existem nesse modo → filtrados no modo Médico).
        {
          anchor: "cal-clinic-select",
          title: "Selecione a(s) clínica(s)",
          description:
            "No modo Clínica, escolha aqui de quais clínicas quer ver a agenda. As clínicas sem Conta Google vinculada aparecem desabilitadas com o aviso “⚠ Conta Google não vinculada” — vincule-as em “Minhas Clínicas” para poder selecioná-las.",
        },
        {
          anchor: "cal-doctors",
          title: "Filtre por médico",
          description:
            "Esta barra lista os médicos da(s) clínica(s) selecionada(s). Clique nos nomes para mostrar ou ocultar a agenda de cada profissional — útil para focar em um médico específico.",
        },
        {
          anchor: "agenda-calendario",
          title: "Sua agenda",
          description:
            "Aqui ficam os atendimentos. Clique num horário livre para agendar rápido naquele momento, ou clique num evento existente para ver os detalhes, editar ou cancelar.",
        },
      );

      return steps;
    },

    async loadFitInCount() {
      try {
        const res = await getPendingFitIns();
        this.fitInPendingCount = res.pendingCount;
      } catch (e) {
        this.fitInPendingCount = 0;
      }
    },

    onDateSelected(date) {
      // ✅ mantém a fonte única do dia selecionado
      this.calendarPageSelectedDate = date;
      this.selectedView = "day";
    },

    selectMenuOptionCalendar(view) {
      this.selectedView = view;
    },

    updateCalendar() {
      // ✅ só chama se existir (year/day-kanban pode não ter fetchEvents)
      const ref = this.$refs.calendarPageContent;
      if (ref && typeof ref.fetchEvents === "function") {
        ref.fetchEvents();
      }
    },

    /** Compromisso do médico — salvia-772. Sem argumento, cria um novo. */
    openCommitmentDialog(compromisso = null) {
      this.commitmentDialog.item = compromisso;
      this.commitmentDialog.visible = true;
    },

    openAddEventDialog() {
      this.openDialogAddNewEvent = true;
    },

    handleTimeClicked(timeEvent) {
      this.selectedTimeForNewEvent = timeEvent;
      this.openDialogAddNewEvent = true;
    },

    openAddNewUserDialog() {
      this.addNewUserDialog.visible = true;
      this.addNewUserDialog.userId = null;
      this.addNewUserDialog.targetType = "patient";
    },

    openPautaDialog() {
      this.pautaDialog.visible = true;
    },

    resetToToday() {
      const today = getLocalDateISO();
      this.calendarPageSelectedDate = today;

      // força “re-render” do mini calendário sem usar setTimeout estranho
      this.pickerDate = today;
      this.$nextTick(() => {
        this.pickerDate = null;
      });
    },

    resetToDayType() {
      this.selectedView = "day";
    },

    switchToDayMode(date) {
      this.calendarPageSelectedDate = date;
      this.selectedView = "day";
    },

    async authenticateWithGoogle() {
      try {
        const { data } = await http.get("/api/auth/google/redirect");
        if (data?.redirect_url) {
          window.location.assign(data.redirect_url);
          return;
        }
        throw new Error("URL de redirecionamento não recebida do servidor");
      } catch (error) {
        console.error("Erro durante autenticação com Google:", error);
      }
    },
  },
};
</script>

<style scoped>
/* ═══════════════════════════════════════
   Drawer lateral — mesmo padrão dos cards
   ═══════════════════════════════════════ */
.drawer-section-header {
  display: flex;
  align-items: center;
  padding: 12px 16px 6px 16px;
}

.drawer-section-title {
  font-size: 11px;
  font-weight: 700;
  color: var(--ds-primary);
  text-transform: uppercase;
  letter-spacing: 0.8px;
}

/* Date picker — garantir que fique centralizado e sem margens estranhas */
.date-picker-wrapper {
  padding: 4px 8px 8px 8px;
  display: flex;
  justify-content: center;
}

.date-picker-wrapper .v-picker {
  box-shadow: none !important;
  border-radius: 6px;
}

/* ═══ Botões de ação coloridos (atalhos rápidos) ═══ */
.action-item {
  border-radius: 8px !important;
  min-height: 44px !important;
  margin-bottom: 8px !important;
  padding-left: 12px !important;
  padding-right: 12px !important;
  transition: transform 0.15s ease, box-shadow 0.15s ease;
}

.action-item:last-child {
  margin-bottom: 0 !important;
}

.action-item:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.12);
}

.action-primary {
  background-color: var(--ds-primary) !important;
}

.action-success {
  background-color: var(--ds-success-light) !important;
}

.action-commitment {
  /* Laranja: a MESMA identidade do compromisso no calendário e no seletor de
     horário, para a pessoa reconhecer o mesmo conceito nos três lugares. */
  background: linear-gradient(135deg, #ea580c 0%, #c2410c 100%);
}
.action-warning {
  background-color: var(--ds-warning-light) !important;
}

.action-fitin {
  background-color: var(--ds-cat-mint) !important;
}

.action-icon {
  min-width: 36px !important;
  margin-top: 8px !important;
  margin-bottom: 8px !important;
  margin-right: 8px !important;
  align-self: center !important;
}

.action-title {
  font-size: 13px !important;
  font-weight: 600 !important;
  color: white !important;
  letter-spacing: 0.2px;
}

/* ═══ Items da lista de programação (mesmo padrão do dashboard) ═══ */
.info-icon {
  min-width: 36px !important;
  margin-top: 8px !important;
  margin-bottom: 8px !important;
  margin-right: 8px !important;
  align-self: center !important;
}

.section-list .section-item {
  min-height: 44px !important;
  padding-left: 16px !important;
  padding-right: 12px !important;
  border-radius: 0;
  transition: background-color 0.15s ease, padding-left 0.15s ease;
}

.section-list .section-item:hover {
  background-color: var(--ds-blue-bg) !important;
  padding-left: 20px !important;
}

.section-title {
  font-size: 13px !important;
  font-weight: 600 !important;
  color: var(--ds-text-700) !important;
}

/* Item ativo — destaque azul suave e barra lateral */
.section-list .active-item {
  background-color: var(--ds-blue-100) !important;
  border-left: 3px solid var(--ds-primary);
  padding-left: 13px !important;
}

.section-list .active-item .section-title {
  color: var(--ds-primary) !important;
  font-weight: 700 !important;
}

.section-list .active-item:hover {
  padding-left: 17px !important;
}

.blurred-container {
  filter: blur(6px);
  z-index: 0;
  pointer-events: none;
}
</style>
